import { defineVariants } from "@/runtime/style-engine/define-variants";

/**
 * Tailwind-like class variants for the `<e-checkbox>` root container.
 */
export const checkboxRootClasses = defineVariants({
  base: `
    e-checkbox
    inline-grid grid-cols-[auto_1fr] gap-x-2 gap-y-1
  `,

  variants: {
    disabled: {
      true: `opacity-50 cursor-not-allowed`,
      false: "cursor-pointer",
    },
    encased: {
      true: "grid w-full p-3 cursor-pointer",
      false: "",
    },
  },

  defaults: {
    disabled: false,
    encased: false,
  },
});

/**
 * Tailwind-like class variants for the checkbox box (button element).
 */
export const checkboxBoxClasses = defineVariants({
  base: `
    e-checkbox-box
    flex-root items-center justify-center
    rounded
    box-border
    cursor-pointer
    outline-none
    transition-colors
    focus-visible:outline-1 focus-visible:outline-solid focus-visible:outline-(--color-foreground) focus-visible:outline-offset-4
    shrink-0
  `,

  variants: {
    color: {
      primary: `[--checkbox-color:var(--color-primary)]`,
      secondary: `[--checkbox-color:var(--color-secondary)]`,
      accent: `[--checkbox-color:var(--color-accent)]`,
      error: `[--checkbox-color:var(--color-error)]`,
      warning: `[--checkbox-color:var(--color-warning)]`,
      info: `[--checkbox-color:var(--color-info)]`,
      success: `[--checkbox-color:var(--color-success)]`,
      neutral: `[--checkbox-color:var(--color-neutral)]`,
      mono: `[--checkbox-color:var(--color-foreground)]`,
    },

    variant: {
      solid: "",
      outline: "border-(--checkbox-color)",
      soft: "",
      subtle: "",
    },

    size: {
      xs: `size-4 my-1`,
      sm: `size-4.5 my-0.75`,
      md: `size-5 my-0.5`,
      lg: `size-5.5 my-0.25`,
      xl: `size-6`,
    },

    disabled: {
      true: `opacity-50 cursor-not-allowed!`,
      false: "",
    },

    checked: {
      true: "",
      false: "",
      indeterminate: "",
    },
  },

  defaults: {
    color: "neutral",
    variant: "subtle",
    size: "md",
    disabled: false,
    checked: false,
  },

  compoundVariants: [
    {
      variant: "solid",
      checked: "false",
      class: "bg-(--checkbox-color)/50 text-(--color-foreground)",
    },
    {
      variant: "solid",
      checked: "true",
      class: "bg-(--checkbox-color) text-(--color-foreground)",
    },
    {
      variant: "solid",
      checked: "indeterminate",
      class: "bg-(--checkbox-color)/50 text-(--checkbox-color)",
    },
    {
      variant: "outline",
      checked: ["true", "indeterminate"],
      class: "text-(--checkbox-color)",
    },
    {
      variant: "soft",
      checked: "false",
      class: "bg-(--checkbox-color)/25",
    },
    {
      variant: "soft",
      checked: "true",
      class: "bg-(--checkbox-color)/50 text-(--color-foreground)",
    },
    {
      variant: "soft",
      checked: "indeterminate",
      class: "bg-(--checkbox-color)/25 text-(--checkbox-color)",
    },
    {
      variant: "subtle",
      checked: "false",
      class: "bg-(--checkbox-color)/25 border-(--checkbox-color)/50 text-(--color-foreground)",
    },
    {
      variant: "subtle",
      checked: "true",
      class: "bg-(--checkbox-color)/50 border-(--checkbox-color)/75 text-(--color-foreground)",
    },
    {
      variant: "subtle",
      checked: "indeterminate",
      class: "bg-(--checkbox-color)/25 border-(--checkbox-color)/50 text-(--checkbox-color)",
    },
    {
      variant: ["subtle", "outline"],
      class: "border",
    },
    {
      color: "mono",
      checked: "true",
      variant: "*",
      class: "bg-(--checkbox-color)! text-(--color-background)!",
    },
  ],
});

/**
 * Classes for the checkbox label wrapper.
 */
export const checkboxLabelClasses = `
  e-checkbox-label
  flex-1
  select-none
  flex
  flex-col
  gap-1
`;

/**
 * Classes for the checkbox label text.
 */
export const checkboxLabelTextClasses = `
  e-checkbox-label-text
  col-start-2
  text-sm
  leading-none
  peer-disabled:cursor-not-allowed
  peer-disabled:opacity-70
`;

/**
 * Classes for the checkbox description text.
 */
export const checkboxDescriptionClasses = `
  e-checkbox-description
  col-start-2
  text-xs
  text-(--color-elevation-600)
`;

/**
 * Classes for the checkbox indicator wrapper.
 */
export const checkboxIndicatorWrapperClasses = `
  flex items-center justify-center
  w-full h-full
`;

/**
 * Tailwind-like class variants for `<e-checkbox-indicator>`.
 */
export const checkboxIndicatorClasses = defineVariants({
  base: ``,

  variants: {
    visible: {
      true: "",
      false: "invisible",
    },
  },

  defaults: {
    visible: "true",
  },
});

/**
 * Tailwind-like class variants for `<e-checkbox-group>`.
 */
export const checkboxGroupClasses = defineVariants({
  base: `flex flex-col gap-2`,

  variants: {
    disabled: {
      true: `opacity-50`,
      false: ``,
    },
  },

  defaults: {
    disabled: false,
  },
});
